import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/Button';
import { motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { FormSuccessPopup } from './FormSuccessPopup';
import { AuroraBackground } from './ui/AuroraBackground';
import { useIsMobile } from '../hooks/useIsMobile';
export function FinalCTASection() {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    businessType: '',
    customBusinessType: '',
    countryCode: '+44',
    phoneNumber: '',
    bestTimeToCall: '',
    serviceInterest: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const countryOptions = [{
    code: '+44',
    country: 'UK',
    flag: '🇬🇧',
    label: 'United Kingdom'
  }, {
    code: '+1',
    country: 'US',
    flag: '🇺🇸',
    label: 'United States'
  }, {
    code: '+1',
    country: 'CA',
    flag: '🇨🇦',
    label: 'Canada'
  }, {
    code: '+61',
    country: 'AU',
    flag: '🇦🇺',
    label: 'Australia'
  }, {
    code: '+33',
    country: 'EU',
    flag: '🇪🇺',
    label: 'Europe'
  }];
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const validatePhone = (phone: string): boolean => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };
  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address'
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
    }
  };
  const handlePhoneBlur = () => {
    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: 'Please enter a valid phone number (10 digits minimum)'
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        phoneNumber: ''
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValid = validateEmail(formData.email);
    const phoneValid = validatePhone(formData.phoneNumber);
    if (!emailValid || !phoneValid) {
      setErrors({
        email: !emailValid ? 'Please enter a valid email address' : '',
        phoneNumber: !phoneValid ? 'Please enter a valid phone number (10 digits minimum)' : ''
      });
      return;
    }
    setIsSubmitting(true);
    const fullPhone = `${formData.countryCode} ${formData.phoneNumber}`;
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: '811422d7-ceb4-4e48-af50-414ea9cd2a55',
          subject: `🔔 New Lead: ${formData.name} - ${formData.serviceInterest}`,
          from_name: 'Strattigize Contact Form',
          name: formData.name,
          email: formData.email,
          phone: fullPhone,
          best_time: formData.bestTimeToCall,
          business_type: formData.businessType === 'Other' ? `Other: ${formData.customBusinessType}` : formData.businessType,
          service_interest: formData.serviceInterest,
          message: `
New Service Request

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 CONTACT INFORMATION
Name: ${formData.name}
Email: ${formData.email}
Phone: ${fullPhone}

📞 CALL PREFERENCES
Best Time to Call: ${formData.bestTimeToCall}

🏢 BUSINESS DETAILS
Business Type: ${formData.businessType === 'Other' ? `Other: ${formData.customBusinessType}` : formData.businessType}

🎯 SERVICE INTERESTED IN
${formData.serviceInterest}

📅 SUBMISSION TIME
${new Date().toLocaleString('en-GB', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
          })}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚡ ACTION REQUIRED: Please reach out within 24 hours.
          `.trim()
        })
      });
      const result = await response.json();
      if (result.success) {
        setShowSuccessPopup(true);
        setFormData({
          name: '',
          email: '',
          businessType: '',
          customBusinessType: '',
          countryCode: '+44',
          phoneNumber: '',
          bestTimeToCall: '',
          serviceInterest: ''
        });
        setErrors({
          email: '',
          phoneNumber: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again or contact us at strattigize@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'businessType' && value !== 'Other' ? {
        customBusinessType: ''
      } : {})
    }));
    if (e.target.name === 'email' && errors.email) {
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
    }
    if (e.target.name === 'phoneNumber' && errors.phoneNumber) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: ''
      }));
    }
  };
  return <section id="contact" className="py-16 md:py-24 gradient-cta-animated relative overflow-hidden" data-id="element-58">
      {/* Aurora Background - Desktop only */}
      {!isMobile && <AuroraBackground className="absolute inset-0 z-0" data-id="element-59" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-black/10 z-[1]" data-id="element-60"></div>

      {/* Decorative Shapes - Desktop only */}
      {!isMobile && <>
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 z-[1]" data-id="element-61"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 z-[1]" data-id="element-62"></div>
        </>}

      <div className="container mx-auto px-4 relative z-10" data-id="element-63">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-64">
          <div className="text-white text-center lg:text-left" data-id="element-65">
            <h2 className="text-3xl md:text-5xl font-black mb-4 md:mb-6 leading-tight" data-id="element-66">
              READY TO SAVE TIME & MAKE MORE MONEY?
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-6 md:mb-8 font-medium" data-id="element-67">
              Pick your plan and let automation do the heavy lifting.
            </p>
            <div className="flex flex-col space-y-3 md:space-y-4 max-w-sm mx-auto lg:mx-0" data-id="element-68">
              <div className="flex items-center space-x-3" data-id="element-69">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 flex items-center justify-center font-bold text-lg md:text-xl" data-id="element-70">
                  1
                </div>
                <p className="font-bold text-sm md:text-base" data-id="element-71">Pick your plan</p>
              </div>
              <div className="flex items-center space-x-3" data-id="element-72">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 flex items-center justify-center font-bold text-lg md:text-xl" data-id="element-73">
                  2
                </div>
                <p className="font-bold text-sm md:text-base" data-id="element-74">
                  We set everything up
                </p>
              </div>
              <div className="flex items-center space-x-3" data-id="element-75">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 flex items-center justify-center font-bold text-lg md:text-xl" data-id="element-76">
                  3
                </div>
                <p className="font-bold text-sm md:text-base" data-id="element-77">
                  You save time & make money
                </p>
              </div>
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.9,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl" data-id="element-78">
            <h3 className="text-xl md:text-2xl font-black text-black mb-4 md:mb-6 text-center" data-id="element-79">
              GET STARTED TODAY
            </h3>
            <form className="space-y-3 md:space-y-4" onSubmit={handleSubmit} data-id="element-80">
              <div data-id="element-81">
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1" data-id="element-82">
                  Full Name
                </label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-sm md:text-base" placeholder="John Doe" data-id="element-83" />
              </div>

              <div data-id="element-84">
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1" data-id="element-85">
                  Email Address
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleEmailBlur} required className={`w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-sm md:text-base`} placeholder="john@company.com" data-id="element-86" />
                {errors.email && <p className="text-red-500 text-xs mt-1 font-medium" data-id="element-87">
                    {errors.email}
                  </p>}
              </div>

              <div data-id="element-88">
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1" data-id="element-89">
                  Phone Number
                </label>
                <div className="flex gap-2" data-id="element-90">
                  <div className="relative" ref={countryDropdownRef} data-id="element-91">
                    <button type="button" onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)} className="w-[72px] h-full py-2.5 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all bg-white flex items-center justify-center gap-1" data-id="element-92">
                      <span className="text-xl md:text-2xl leading-none" data-id="element-93">
                        {countryOptions.find(o => o.code === formData.countryCode)?.flag || '🇬🇧'}
                      </span>
                      <ChevronDownIcon size={12} className="text-gray-400 flex-shrink-0" data-id="element-94" />
                    </button>
                    {isCountryDropdownOpen && <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-48 py-1 max-h-48 overflow-y-auto" data-id="element-95">
                        {countryOptions.map(option => <button key={`${option.country}-${option.code}`} type="button" onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        countryCode: option.code
                      }));
                      setIsCountryDropdownOpen(false);
                    }} className={`w-full px-3 py-2 flex items-center gap-3 text-left transition-colors ${formData.countryCode === option.code && formData.countryCode === option.code ? 'bg-primary/5 text-primary' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-96">
                            <span className="text-xl leading-none" data-id="element-97">
                              {option.flag}
                            </span>
                            <span className="text-sm font-medium" data-id="element-98">
                              {option.label}
                            </span>
                            <span className="text-xs text-gray-400 ml-auto" data-id="element-99">
                              {option.code}
                            </span>
                          </button>)}
                      </div>}
                  </div>
                  <div className="flex-1" data-id="element-100">
                    <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} onBlur={handlePhoneBlur} required className={`w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-sm md:text-base`} placeholder="07123 456789" data-id="element-101" />
                  </div>
                </div>
                {errors.phoneNumber && <p className="text-red-500 text-xs mt-1 font-medium" data-id="element-102">
                    {errors.phoneNumber}
                  </p>}
              </div>

              <div data-id="element-103">
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1" data-id="element-104">
                  Plan Interested In
                </label>
                <select name="serviceInterest" value={formData.serviceInterest} onChange={handleChange} required className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all bg-white text-sm md:text-base" data-id="element-105">
                  <option value="" data-id="element-106">Select plan...</option>
                  <option value="Starter (£97/mo)" data-id="element-107">Starter (£97/mo)</option>
                  <option value="Unlimited (£297/mo)" data-id="element-108">
                    Unlimited (£297/mo)
                  </option>
                  <option value="Pro (£497/mo)" data-id="element-109">Pro (£497/mo)</option>
                </select>
              </div>

              <div data-id="element-110">
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1" data-id="element-111">
                  Best Time to Call
                </label>
                <select name="bestTimeToCall" value={formData.bestTimeToCall} onChange={handleChange} required className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all bg-white text-sm md:text-base" data-id="element-112">
                  <option value="" data-id="element-113">Select preferred time...</option>
                  <option value="Morning (9AM - 12PM)" data-id="element-114">
                    Morning (9AM - 12PM)
                  </option>
                  <option value="Afternoon (12PM - 5PM)" data-id="element-115">
                    Afternoon (12PM - 5PM)
                  </option>
                  <option value="Evening (5PM - 8PM)" data-id="element-116">
                    Evening (5PM - 8PM)
                  </option>
                </select>
              </div>

              <div data-id="element-117">
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1" data-id="element-118">
                  Business Type
                </label>
                <select name="businessType" value={formData.businessType} onChange={handleChange} required className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all bg-white text-sm md:text-base" data-id="element-119">
                  <option value="" data-id="element-120">Select your industry...</option>
                  <option value="Roofing Contractors" data-id="element-121">
                    Roofing Contractors
                  </option>
                  <option value="Kitchen Remodelers" data-id="element-122">Kitchen Remodelers</option>
                  <option value="Plumber" data-id="element-123">Plumber</option>
                  <option value="Electrician" data-id="element-124">Electrician</option>
                  <option value="HVAC Services" data-id="element-125">HVAC Services</option>
                  <option value="General Contractor" data-id="element-126">General Contractor</option>
                  <option value="Landscaping" data-id="element-127">Landscaping</option>
                  <option value="Home Services" data-id="element-128">Home Services</option>
                  <option value="Cleaning Services" data-id="element-129">Cleaning Services</option>
                  <option value="Auto Repair" data-id="element-130">Auto Repair</option>
                  <option value="Medical / Dental" data-id="element-131">Medical / Dental</option>
                  <option value="Med Spa" data-id="element-132">Med Spa</option>
                  <option value="Barber Shop" data-id="element-133">Barber Shop</option>
                  <option value="Salon / Beauty" data-id="element-134">Salon / Beauty</option>
                  <option value="Restaurant / Cafe" data-id="element-135">Restaurant / Cafe</option>
                  <option value="Fitness / Gym" data-id="element-136">Fitness / Gym</option>
                  <option value="Real Estate" data-id="element-137">Real Estate</option>
                  <option value="Legal" data-id="element-138">Legal</option>
                  <option value="Accounting / Finance" data-id="element-139">
                    Accounting / Finance
                  </option>
                  <option value="Other" data-id="element-140">Other</option>
                </select>
                {formData.businessType === 'Other' && <input type="text" name="customBusinessType" value={formData.customBusinessType} onChange={handleChange} required className="w-full mt-2 px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-sm md:text-base" placeholder="Enter your business type..." data-id="element-141" />}
              </div>

              <Button type="submit" fullWidth size="lg" className="mt-3 md:mt-4 text-base md:text-lg" disabled={isSubmitting} data-id="element-142">
                {isSubmitting ? 'SUBMITTING...' : 'GET STARTED'}
              </Button>

              <p className="text-center text-[10px] md:text-xs text-gray-500 mt-3 md:mt-4" data-id="element-143">
                Your information is secure and will not be shared with any third
                parties 🔒
              </p>
            </form>
          </motion.div>
        </div>
      </div>

      <FormSuccessPopup isOpen={showSuccessPopup} onClose={() => setShowSuccessPopup(false)} data-id="element-144" />
    </section>;
}